import { useEffect, useRef, useCallback, useMemo, memo } from 'react'
import SwiperCore from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination } from 'swiper/modules'
import { isMobile } from 'react-device-detect'

import { getParsedLocalStorageItem } from '../../../js/script'
import { getAccessToken } from '../../../api/auth'
import { handlePostMessage } from '../../../js/postMessage'

import { MyPayInfo } from '../../../@types/cart'
import { PaymentInfoItem } from '../../../@types/order'
import { BtnBlack } from '../../Button'
import { IconPlus } from '../../svg/IconSvg'

import { useCommonContext } from '../../provider/CommonProvider'
import { useAppContext } from '../../provider/AppProvider'
import { useMyContext } from '../../provider/MyProvider'

interface AFPayCardSwiperProps {
  isMember: boolean
  cardInfos: PaymentInfoItem[]
  setCardInfo: React.Dispatch<React.SetStateAction<PaymentInfoItem | null>>
  myPayData?: MyPayInfo
  payUserRegister: string
}

const AFPayCardSwiper = ({ isMember, cardInfos, setCardInfo, myPayData, payUserRegister }: AFPayCardSwiperProps) => {
  const { isAppView } = useAppContext()
  const { hasAccessToken, setIsLogin } = useCommonContext()
  const { setToastMessage, setIsToast, setToastBtnMessage, setToastEvent } = useMyContext()

  const swiperRef = useRef<SwiperCore | null>(null)

  // 브릿지/새창 공통 함수
  const openBridgeOrWindow = useCallback(({ url, isAppView }: { url: string; isAppView: boolean }) => {
    if (isAppView) {
      handlePostMessage({
        actionType: 'onLink',
        params: { url },
      })
    } else {
      const open = window.open('about:blank', '_blank')
      if (!open) return
      open.location.href = url
    }
  }, [])

  // NCPPay 관련 공통 함수
  const callNcpPay = useCallback(
    ({
      option,
      type,
      customCssUrl,
      payName,
      logoImageUrl,
    }: {
      option: any
      type: 'REGISTER_USER_WITH_PAYMENT' | 'REGISTER_PAYMENT'
      customCssUrl?: string
      payName?: string
      logoImageUrl?: string
    }) => {
      if (!window.NCPPay) return
      window.NCPPay.setMyPayCssCustom(
        customCssUrl ? `https://shop-api.e-ncp.com${customCssUrl}` : undefined,
        null,
        payName,
        logoImageUrl
      )
      window.NCPPay.setConfiguration({
        clientId: process.env.REACT_APP_CLIENT_ID,
        confirmUrl: `${process.env.REACT_APP_SHOP_URL}/closet`,
        platform: isMobile ? 'MOBILE_WEB' : 'PC',
        accessToken: hasAccessToken ? getAccessToken() : '',
        withCredentials: true,
      })
      window.NCPPay.requestMyPayApi(option, type)

      console.log('NCPPay 호출:', {
        option,
        type,
        customCssUrl,
        payName,
        logoImageUrl,
      })
    },
    [hasAccessToken, myPayData]
  )

  // 회원 등록 처리
  const handleUserRegister = useCallback(
    (isWeb: boolean, getUrlParams: () => string) => {
      if (isWeb) {
        callNcpPay({
          option: {
            clientReturnUrl: `${process.env.REACT_APP_SHOP_URL}/closet`,
            clientBridgeUrl: `${process.env.REACT_APP_SHOP_URL}/addcard`,
          },
          type: 'REGISTER_USER_WITH_PAYMENT',
          customCssUrl: myPayData?.customCssUrl,
          payName: myPayData?.payName,
        })
      } else {
        openBridgeOrWindow({
          url: `${process.env.REACT_APP_SHOP_URL}/myPayReady?${getUrlParams()}`,
          isAppView,
        })
      }
    },
    [callNcpPay, myPayData, openBridgeOrWindow, isAppView]
  )

  // 카드 등록 처리
  const handleCardRegister = useCallback(
    (isWeb: boolean, getUrlParams: () => string) => {
      if (!hasAccessToken) {
        setIsToast(true)
        setToastMessage('로그인이 필요한 서비스입니다.')
        setToastBtnMessage('로그인')
        setToastEvent(() => () => setIsLogin(true))
        return
      }
      if (isWeb) {
        callNcpPay({
          option: {
            clientReturnUrl: `${process.env.REACT_APP_SHOP_URL}/closet`,
            payMethod: '01',
          },
          type: 'REGISTER_PAYMENT',
          customCssUrl: myPayData?.customCssUrl,
          payName: myPayData?.payName,
          logoImageUrl: myPayData?.logoImageUrl,
        })
      } else {
        openBridgeOrWindow({
          url: `${process.env.REACT_APP_SHOP_URL}/addcard?${getUrlParams()}`,
          isAppView,
        })
      }
    },
    [
      hasAccessToken,
      setIsToast,
      setToastMessage,
      setToastBtnMessage,
      setToastEvent,
      setIsLogin,
      callNcpPay,
      myPayData,
      openBridgeOrWindow,
      isAppView,
    ]
  )

  const handleRegisterClick = useCallback(
    ({ notRegisterUser }: { notRegisterUser: boolean }) => {
      const accessToken = getParsedLocalStorageItem('accessToken')
      const aboutToken = getParsedLocalStorageItem('aboutAccessToken')
      const isWeb = !isAppView && !isMobile

      // 공통 URL 파라미터 생성
      const getUrlParams = () =>
        `token=${aboutToken}&payLogo=${encodeURIComponent(JSON.stringify(myPayData))}&accesstoken=${accessToken}&app=${
          isAppView ? 'true' : 'false'
        }`

      console.log('getUrlParams >>> ', getUrlParams())

      // 회원 등록 여부에 따라 처리
      if (notRegisterUser) handleUserRegister(isWeb, getUrlParams)
      else handleCardRegister(isWeb, getUrlParams)
    },
    [isAppView, myPayData, handleUserRegister, handleCardRegister]
  )

  useEffect(() => {
    if (cardInfos.length > 0) {
      setCardInfo(cardInfos[0])
    } else {
      setCardInfo(null)
    }
  }, [cardInfos, setCardInfo])

  const renderRegisterButton = useCallback(() => {
    const notRegisterUser = payUserRegister !== '0000'
    return (
      <BtnBlack
        style={{
          display: 'flex',
          gap: 4,
          fontSize: '13px',
          fontWeight: 600,
          lineHeight: '16px',
          padding: '10px 16px 10px 10px',
        }}
        onClick={() => handleRegisterClick({ notRegisterUser })}
      >
        <IconPlus width={16} height={16} color='var(--colWhite)' />
        {notRegisterUser ? '회원 등록하기' : '카드 등록하기'}
      </BtnBlack>
    )
  }, [payUserRegister, handleRegisterClick])

  const slides = useMemo(
    () =>
      hasAccessToken &&
      cardInfos?.map((cardInfo: PaymentInfoItem, index: number) => (
        <SwiperSlide
          key={cardInfo?.cardImgUrl}
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px',
            cursor: 'pointer',
          }}
          onClick={() => {
            swiperRef.current?.slideTo(index)
          }}
        >
          {isMember ? (
            <div
              className='card-image-box'
              style={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                borderRadius: '15px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundImage: `url(${cardInfo?.cardImgUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            ></div>
          ) : (
            <img
              src={cardInfo?.cardImgUrl}
              alt=''
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '15px',
              }}
            />
          )}
        </SwiperSlide>
      )),
    [hasAccessToken, cardInfos, isMember]
  )

  const registerSlide = useMemo(
    () =>
      isMember && (
        <SwiperSlide
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px',
            cursor: 'pointer',
          }}
          onClick={() => {
            swiperRef.current?.slideTo(cardInfos?.length ?? 0)
          }}
        >
          <div
            className='card-image-box'
            style={{
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              borderRadius: '15px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              background: '#f5f5f5',
            }}
          >
            <img src={`/images/icon_afpay_card.png`} alt='어바웃피싱 페이 로고' />
            <p>
              어바웃피싱 페이 등록하고 <br />
              빠르게 결제하세요.
            </p>
            {renderRegisterButton()}
          </div>
        </SwiperSlide>
      ),
    [isMember, cardInfos?.length, renderRegisterButton]
  )

  if (!myPayData) return null

  return (
    <div className='select_card_wrap'>
      <Swiper
        modules={[Pagination]}
        spaceBetween={18}
        slidesPerView={isMobile ? 1.1 : 1.4}
        centeredSlides={isMobile}
        loop={false}
        onSlideChange={(swiper: SwiperCore) => {
          console.log('Slide index changed:', cardInfos[swiper.activeIndex] || null)
          setCardInfo(cardInfos[swiper.activeIndex] || null)
        }}
        onSwiper={(swiper: SwiperCore) => {
          swiperRef.current = swiper
        }}
      >
        {slides}
        {registerSlide}
      </Swiper>
    </div>
  )
}

export default memo(AFPayCardSwiper)
